import React, { useState } from "react";
import { toast } from "react-toastify";
import Search from "../../components/form/Search";
import Button from "../../components/form/Button";
import Table from "../../components/elements/table/Table";
import { setTitle } from "../../helpers/MetaTag";
import { handleFilterChange } from "../../helpers";
import DeleteModal from "../../components/common/DeleteModal";

import useFetch from "../../hooks/useFetch";
import { useDebounce } from "../../hooks/useDebounce";
import LimitSelect from "../../components/elements/LimitSelect";
import useMutation from "../../hooks/useMutation";
import Switch from "../../components/form/Switch";
import Badge from "../../components/elements/Badge";
import {
  categoryDelete,
  categoryList,
  categoryStatusChange,
} from "../../services/catagoryService";
import ManageCategoryModal from "../../components/sections/admin/category/modals/ManageCategoryModal";
import { useParams } from "react-router-dom";
import { useGetPermission } from "../../hooks/useGetPermission";

const Category = () => {
  setTitle("Vpeters Admin | Application Form");
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const { categoryType } = useParams();
  const permissionKey =
    categoryType === "publication"
      ? "publicationCategories"
      : "blogsCategories";
  const { permission, status } = useGetPermission(permissionKey);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { data, error, isLoading, updateData, isFetching, refetch } = useFetch({
    dependencies: [
      "applicationForm",
      permission?.list,
      filters,
      search,
      categoryType,
    ],
    fetchFunction: async () => {
      const res = await categoryList({
        ...filters,
        type: categoryType,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!permission?.list && status === "ok",
  });

  //   Application Form status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        const res = await categoryStatusChange({
          ids: id,
          status,
        });
        return { res, payload: { id, status } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          updateData((pre) => {
            pre.data = (pre?.data || []).map((elm) =>
              elm?._id === payload?.id
                ? { ...elm, status: payload?.status === 1 ? 2 : 1 }
                : elm
            );
            return { ...pre };
          });
          toast.warning(res?.message || res?.error);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const tableHeadData = [
    {
      _id: 1,
      name: "Category Name",
      align: "left",
    },
    {
      _id: 2,
      name: "Category Type",
      align: "left",
    },
    {
      _id: 3,
      name: "Status",
      align: "left",
    },
    ...(permission?.edit || permission?.delete
      ? [
          {
            _id: 4,
            name: "Action",
            align: "left",
          },
        ]
      : []),
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            (
              <div
                dangerouslySetInnerHTML={{
                  __html: item?.name,
                }}
              ></div>
            ) || "-",
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            (
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    item?.type?.charAt(0).toUpperCase() +
                      item?.type?.slice(1) || "-",
                }}
              ></div>
            ) || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Switch
                className={`!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px] ${
                  !permission?.changeStatus ? "opacity-60" : ""
                }`}
                checked={item?.status === 1}
                onChange={() => {
                  updateData((pre) => {
                    pre.data = (pre?.data || []).map((elm) =>
                      elm?._id === item?._id
                        ? { ...elm, status: elm?.status === 1 ? 2 : 1 }
                        : elm
                    );
                    return { ...pre };
                  });
                  statusChangeHandler({
                    id: item?._id,
                    status: item?.status === 1 ? 2 : 1,
                  });
                }}
                disabled={!permission?.changeStatus}
              />
              <Badge
                type={item?.status === 1 ? "success" : "danger"}
                label={item?.status === 1 ? "Active" : "Inactive"}
              />
            </div>
          ),
        },
        ...(permission?.edit || permission?.delete
          ? [
              {
                _id: 5,
                type: "action",
                align: "left",
                cellClass: "w-[5%]",
                actionData: [
                  ...(permission?.edit && status === "ok"
                    ? [
                        {
                          _id: 1,
                          name: "Edit",
                          icon: "fa-regular fa-pen-to-square",
                          onClick: () => {
                            setModalOpen({ isOpen: true, data: item });
                          },
                          standout: false,
                        },
                      ]
                    : []),

                  ...(permission?.delete && status === "ok"
                    ? [
                        {
                          _id: 2,
                          name: "Delete",
                          icon: "fa-regular fa-trash-can",
                          standout: true,
                          onClick: () => {
                            setDeleteModal({
                              isOpen: true,
                              data: item,
                            });
                          },
                        },
                      ]
                    : []),
                ],
              },
            ]
          : []),
      ],
    };
  });

  // add or edit Application Form =================>
  function applicationFormManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          updateData((pre) => ({
            ...pre,
            data: [res?.data, ...(pre?.data || [])],
          }));
          refetch();
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        updateData((pre) => ({
          ...pre,
          data: pre?.data?.map((item) =>
            item?._id === res?.data?._id ? res?.data : item
          ),
        }));
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   Application Form delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await categoryDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        updateData((pre) => {
          pre.data = (pre?.data || [])?.filter(
            (item) => !payload?.includes(item?._id)
          );
          return { ...pre };
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          refetch();
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-bold text-center text-lg xl:text-3xl text-chestnut-rose">
          {categoryType.charAt(0).toUpperCase() + categoryType.slice(1)}{" "}
          Category
        </div>
        <div className="flex items-center gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="w-72">
            <Search
              placeholder={"Search here"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
          {permission?.add && status === "ok" && (
            <div className="relative">
              <Button
                buttonClasses={
                  "!bg-transparent border border-transparent !h-10 hover:!bg-chestnut-rose/10 hover:!border-chestnut-rose/10"
                }
                buttonLabel={"Create New"}
                buttonLabelClasses={"font-medium !text-sm text-chestnut-rose"}
                buttonFunction={() =>
                  setModalOpen({ isOpen: true, data: null })
                }
              />
            </div>
          )}
        </div>
      </div>
      {status === "ok" &&
        (permission?.list ? (
          <Table
            tableHeadData={tableHeadData}
            tableData={tableData}
            isLoder={isLoading}
            pageCount={data?.metadata?.totalPages}
            currentPage={filters?.page}
            onPageChange={(val) => {
              handleFilterChange("page", val, setFilters);
              setSelectedItems([]);
            }}
            handleOrdering={(val) => {
              console.log(val);
            }}
            isFetching={isFetching}
            isOrderingDisabled={true}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            tableActionDropdownContainer={"!w-48"}
          />
        ) : (
          <div className="font-semibold text-xl text-center h-60 flex items-center justify-center text-shades-07">
            You don't have permission to view this list.
          </div>
        ))}
      <ManageCategoryModal
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={applicationFormManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Category;
